import { useState, useEffect, useRef } from "react";
import {
  Button,
  Grid,
  TextInput,
  Select,
  Textarea,
  Group,
  Checkbox,
  Accordion,
  Box,
  Text,
  Badge,
  ActionIcon,
  List,
  Image,
} from "@mantine/core";
import "@mantine/core/styles.css";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createNewRecord,
  getAllData,
  updateRecord,
} from "../../services/common.service";
import { displayNotification } from "../../commonComponents/notifications/displayNotification";
import { COLORS } from "../../constants/colors";
import SelectWithAPI from "../../commonComponents/components/selectWithAPI/SelectWithAPI";
import TimeInputComp from "../../commonComponents/components/dateInput/TimeInputComp";
import { IconTrash, IconPlus, IconCircleFilled } from "@tabler/icons-react";
import DigitalSignature from "../../commonComponents/components/digitalSignature/DigitalSignature";
import DateInputComp from "../../commonComponents/components/dateInput/DateInputComp";
import ModalComp from "../../commonComponents/components/modal/ModalComp";
import dayjs from "dayjs";
import useAuthStore from "../../store/authStore";
import SelectWithCleared from "./../../commonComponents/components/selectWithAPI/SelectWithCleared";

const CrewMovementSheetDrawer = ({ recordId, onClose, mode, showEdit }) => {
  const queryClient = useQueryClient();

  const [sigVisibleCrew, setSigVisibleCrew] = useState(false);
  const [pointersModalOpen, setPointersModalOpen] = useState(false);

  const [viewCrewSignatureModal, setViewCrewSignatureModal] = useState(false);

  const { auth } = useAuthStore()

  const role = auth?.emp_role_id?.role_name?.toLowerCase();
  const empCode = auth?.emp_code;

  // Refs for each signature canvas
  const sigCanvasCrew = useRef(null);

  const [errors, setErrors] = useState({
    in_time: "",
    out_time: "",
    crew_signature: "",
  });

  const acknowledgmentPoints = [
    "I acknowledge that I have read and clearly understood all SEP circulars up to date.",
    "I confirm that my flight time has not been violated in the past 07 / 30 or 365 days.",
    "I confirm that my Competency Card is updated and current.",
    "I confirm that I am updated with all current SOP’s.",
    "I confirm that I have completed my Pre-flight medicals.",
  ];

  const [form, setForm] = useState({
    id: "",
    base: "GOX",
    date: dayjs().format("YYYY-MM-DD"),
    flight_standby: "Flight",
    flights: [],
    crew_name: "",
    emp_id: "",
    position: "",
    in_time: "",
    out_time: "",
    remark: "",
    confirmation: false,
    crew_signature: "", // for crew
  });

  const [original, setOriginal] = useState(null);
  
  const missingAny =
    !form.in_time ||
    !form.out_time ||
    !form.crew_signature;

  const originalHasAllValues =
    original &&
    original.in_time &&
    original.out_time &&
    original.crew_signature;

  const requiredFieldsChanged =
    form.in_time !== original?.in_time ||
    form.out_time !== original?.out_time ||
    form.crew_signature !== original?.crew_signature;

  useEffect(() => {
    if (mode !== "view") return;

    if (role === "scc" || role === "cc") {
      // SCC/CC logic
      showEdit(missingAny); // only show when something missing
    } else {
      // Other roles: always allow edit
      showEdit(true);
    }
  }, [
    mode,
    role,
    missingAny,
    form.in_time,
    form.out_time,
    form.crew_signature,
    showEdit
  ]);

  const [openedFlights, setOpenedFlights] = useState([]);

  // const excludedCockpit = form.flights
  //   .flatMap((f) => [f.captain, f.first_officer, f.observer])
  //   .filter(Boolean);

  const handleAccordionChange = (values) => setOpenedFlights(values);

  const mapFormToDB = (form) => {
    const flightDetails = form.flights.map((f) => ({
      flight_no: f.flight_no,
      sector: f.sector,
      aircraft: f.ac_regn,
      p1_name: f.captain,
      p2_name: f.first_officer,
      p3_name: f.observer,
    }));

    return {
      id: form.id || null,
      base: form.base || "GOX",
      flight_type: form.flight_standby || "STANDBY",
      flight_date: form.date,
      day_of_flight: dayjs(form.date).format("dddd"),
      emp_name: form.crew_name?.split(" - ")[0] || "",
      emp_id: form.emp_id,
      emp_position: form.position,
      in_time: form.in_time,
      out_time: form.out_time || null,
      remarks: form.remark,
      is_acknowledged: form.confirmation,
      crew_signature: form.crew_signature,
      flight_details: JSON.stringify(flightDetails),
    };
  };

  // Fetch record data if editing
  const { data: existingData, isLoading } = useQuery({
    queryKey: ["crewMovementRecord", recordId],
    queryFn: () =>
      getAllData({
        source: "crew_movement_report",
        filter: { id_eq: recordId },
      }),
    enabled: !!recordId,
  });

  useEffect(() => {
    if (existingData?.data?.length > 0) {
      const record = existingData.data[0];
      let flightDetails = [];
      try {
        flightDetails = JSON.parse(record.flight_details || "[]");
      } catch (err) {
        console.error("Error parsing flight_details:", err);
      }

      const originalValues = {
        in_time: record.in_time || "",
        out_time: record.out_time || "",
        crew_signature: record.crew_signature || "",
      };

      setOriginal(originalValues);

      const crewNameWithCode =
        record.emp_name && record.emp_id
          ? `${record.emp_name} - ${record.emp_id}`
          : record.emp_name || null;

      const mapNameWithCode = (name, empId) =>
        name && empId ? `${name} - ${empId}` : name || null;

      setForm({
        id: record.id || null,
        base: record.base || null,
        date: record.flight_date || null,
        flight_standby: record.flight_type || null,
        flights: flightDetails.map((f) => ({
          flight_no: f.flight_no || null,
          sector: f.sector || null,
          ac_regn: f.aircraft || null,
          captain: mapNameWithCode(f.p1_name, f.p1_staff_id) || null,
          first_officer: mapNameWithCode(f.p2_name, f.p2_staff_id) || null,
          observer: mapNameWithCode(f.p3_name, f.p3_staff_id) || null,
        })),
        crew_name: crewNameWithCode,
        emp_id: record.emp_id || null,
        position: record.emp_position || null,
        in_time: record.in_time || null,
        out_time: record.out_time || null,
        remark: record.remarks || null,
        confirmation: record.is_acknowledged || false,
        crew_signature: record.crew_signature,
      });
      return; // prevent auto-fill for edit mode
    }

    if (!recordId && (role === "scc" || role === "cc")) {
      setForm((prev) => ({
        ...prev,
        crew_name: `${auth?.emp_name} - ${auth?.emp_code}`,
        emp_id: auth?.emp_code,
        position: role.toUpperCase(), // SCC or CC
      }));
    }
  }, [existingData, recordId, role, auth]);

  // Mutations
  const createMutation = useMutation({
    mutationFn: (body) =>
      createNewRecord({ body, source: "crew_movement_report" }),
    onSuccess: () => {
      displayNotification({
        message: "Crew Movement created successfully",
        variant: "success",
      });
      queryClient.invalidateQueries(["all-records", "crew_movement_report"]);
      onClose();
    },
    onError: (err) => {
      displayNotification({
        message: err?.message || "Error creating record",
        variant: "error",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (body) =>
      updateRecord({ body, source: "crew_movement_report", id: form.id }),
    onSuccess: () => {
      displayNotification({
        message: "Crew Movement updated successfully",
        variant: "success",
      });
      queryClient.invalidateQueries(["all-records", "crew_movement_report"]);
      onClose();
    },
    onError: (err) => {
      displayNotification({
        message: err?.message || "Error updating record",
        variant: "error",
      });
    },
  });

  const handleChange = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = () => {
    setErrors({
      in_time: "",
      out_time: "",
      crew_signature: "",
    });

    let hasError = false;

    if (!form.in_time) {
      setErrors((prev) => ({ ...prev, in_time: "In Time is required" }));
      hasError = true;
    }
    if (!form.confirmation) {
      hasError = true;
      displayNotification({
        message: "Please confirm before saving",
        variant: "error",
      });
    }

    if (hasError) return;

    const filteredFlights = form.flights.filter(
      (f) =>
        f.flight_no ||
        f.sector ||
        f.ac_regn ||
        f.captain ||
        f.first_officer ||
        f.observer
    );

    const payload = mapFormToDB({ ...form, flights: filteredFlights });

    if (recordId) updateMutation.mutate(payload);
    else createMutation.mutate(payload);
  };

  const handleFlightChange = (index, field, value) => {
    setForm((prev) => {
      const flights = [...prev.flights];
      flights[index][field] = value ?? null;
      return { ...prev, flights };
    });
  };

  const handleAddFlight = () => {
    setForm((prev) => ({
      ...prev,
      flights: [
        ...prev.flights,
        {
          flight_no: "",
          sector: "",
          ac_regn: "",
          captain: "",
          first_officer: "",
          observer: "",
        },
      ],
    }));
    setOpenedFlights((prev) => [...prev, `${form.flights.length}`]);
  };

  const handleRemoveFlight = (index) => {
    setForm((prev) => ({
      ...prev,
      flights: prev.flights.filter((_, i) => i !== index),
    }));
    setOpenedFlights((prev) => prev.filter((val) => val !== `${index}`));
  };

  const onFlightSelect = (row, index) => {
    if (!row) return;

    const updatedFlights = [...form.flights];

    updatedFlights[index] = {
      ...updatedFlights[index],
      id: row.id,
      flight_no: row.flight_number || "",
      sector: `${row?.depart_airport_id?.airport_code || ""} - ${row?.arrive_airport_id?.airport_code || ""
        }`,
      ac_regn: row?.aircraft_id?.tail_reg_no || "",
      captain:
        row?.p1_name && row?.p1_staff_id
          ? `${row.p1_name} - ${row.p1_staff_id}`
          : "",
      first_officer:
        row?.p2_name && row?.p2_staff_id
          ? `${row.p2_name} - ${row.p2_staff_id}`
          : "",
      observer:
        row?.p3_name && row?.p3_staff_id
          ? `${row.p3_name} - ${row.p3_staff_id}`
          : "",
    };
    setForm((prev) => ({ ...prev, flights: updatedFlights }));
  };

  const resetFlightRow = () => ({
    id: null,
    flight_no: null,
    sector: null,
    ac_regn: null,
    captain: null,
    first_officer: null,
    observer: null,
  });

  if (isLoading) return <p>Loading data...</p>;

  const isViewMode = mode === "view";
  // const isEditMode = mode === "edit";

  const selectedFlightIds = form.flights
    .map(f => f.id)
    .filter(Boolean);

  return (
    <Grid>
      <Grid.Col span={4}>
        <TextInput
          label="Base"
          placeholder="Enter base name"
          value={form.base}
          readOnly={isViewMode}
          onChange={(e) => handleChange("base", e.target.value)}
        />
      </Grid.Col>

      <Grid.Col span={4}>
        <Select
          label="Flight / Standby"
          data={["Flight", "Stand By"]}
          clearable
          searchable
          readOnly={isViewMode}
          placeholder="Enter Flight Type"
          value={form.flight_standby}
          onChange={(val) => handleChange("flight_standby", val)}
        />
      </Grid.Col>

      <Grid.Col span={4}>
        <DateInputComp
          label="Date"
          value={form.date ? new Date(form.date) : null}
          onChange={(val) => {
            handleChange("date", val)
            setForm((prev) => ({ ...prev, flights: [] }));
          }}
          maxDate={dayjs().format("YYYY-MM-DD")}
          readOnly={isViewMode}
          clearable={false}
        />
      </Grid.Col>

      <Grid.Col span={4}>
        <TextInput
          label="Day"
          placeholder="Day of week"
          value={form.date ? dayjs(form.date).format("dddd") : ""}
          readOnly
        />
      </Grid.Col>


      <Grid.Col span={12}>
        <Group justify="space-between" mb="sm" align="center">
          <Text weight={500}>Flight Details</Text>
          {!isViewMode && (
            <ActionIcon
              variant="filled"
              size="md"
              onClick={handleAddFlight}
              disabled={form.flights.length >= 6 || isViewMode ? true : false}
            >
              <IconPlus size={20} />
            </ActionIcon>
          )}
        </Group>
      </Grid.Col>

      <Grid.Col span={12}>
        <Accordion
          multiple
          value={openedFlights}
          onChange={handleAccordionChange}
        >
          {form.flights.map((flight, index) => {
            return (
              <Accordion.Item key={index} value={`${index}`}>
                <Accordion.Control>
                  {`${flight.flight_no || "Add flight"}`}
                </Accordion.Control>

                <Accordion.Panel>
                  <Box p="sm">
                    <Grid>
                      <Grid.Col span={4}>
                        <SelectWithAPI
                          label="Flight No"
                          value={flight.flight_no}
                          readOnly={isViewMode}
                          placeholder={"Select Flight No"}
                          onClear={() => {
                            const updated = [...form.flights];
                            updated[index] = resetFlightRow();
                            setForm(prev => ({ ...prev, flights: updated }));
                          }}
                          setValue={(val) => handleFlightChange(index, "flight_no", val)}
                          tableName="journey_leg"
                          onItemSelect={(row) => onFlightSelect(row, index)}   // FULL ROW HERE
                          itemData={{
                            filters: JSON.stringify({
                              journey_date_eq: form?.date ? dayjs(form?.date).format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD"),

                              or:
                                role === "scc" || role === "cc"
                                  ? [
                                    {
                                      "c1_staff_id_eq": empCode,
                                      "c2_staff_id_eq": empCode,
                                    }
                                  ]
                                  : [],

                              id_nin: selectedFlightIds.filter(id => id !== flight.id)
                            }),
                            display_options: `["flight_number","depart_airport_id.airport_code","arrive_airport_id.airport_code"]`,
                            return_value: ["flight_number"]
                          }}
                        />
                      </Grid.Col>

                      <Grid.Col span={4}>
                        <TextInput
                          label="Sector"
                          value={flight?.sector || ""}
                          readOnly={isViewMode}
                          placeholder="Enter sector"
                          onChange={(e) =>
                            handleFlightChange(index, "sector", e.target.value)
                          }
                        />
                      </Grid.Col>

                      <Grid.Col span={4}>
                        <SelectWithAPI
                          label="A/C Regn"
                          value={flight?.ac_regn || null}
                          placeholder={"Select A/C Regn"}
                          readOnly={isViewMode}
                          setValue={(val) =>
                            handleFlightChange(index, "ac_regn", val)
                          }
                          tableName="aircrafts"
                          itemData={{
                            filters: `{"is_active_eq": true, "page": -1}`,
                            display_options: `["tail_reg_no"]`,
                            return_value: "tail_reg_no",
                          }}
                        />
                      </Grid.Col>

                      <Grid.Col span={4}>
                        <SelectWithCleared
                          label="Captain"
                          value={flight?.captain || null}
                          placeholder="Select Captain"
                          readOnly={isViewMode}
                          setValue={(val) =>
                            handleFlightChange(index, "captain", val)
                          }
                          tableName="users"
                          itemData={{
                            filters: JSON.stringify({
                              is_active_eq: true,
                              "emp_category_id.category_name_eq": "Cockpit",
                            }),
                            display_options: `["emp_name", "emp_code"]`,
                            return_value: ["emp_name", "emp_code"],
                          }}
                        />
                      </Grid.Col>

                      <Grid.Col span={4}>
                        <SelectWithCleared
                          label="First Officer"
                          value={flight?.first_officer}
                          placeholder="Select First Officer"
                          readOnly={isViewMode}
                          setValue={(val) =>
                            handleFlightChange(index, "first_officer", val)
                          }
                          tableName="users"
                          itemData={{
                            filters: JSON.stringify({
                              is_active_eq: true,
                              "emp_category_id.category_name_eq": "Cockpit",
                            }),
                            display_options: `["emp_name", "emp_code"]`,
                            return_value: ["emp_name", "emp_code"],
                          }}
                        />
                      </Grid.Col>

                      <Grid.Col span={4}>
                        <SelectWithCleared
                          label="Observer"
                          placeholder="Select Observer"
                          value={flight?.observer}
                          readOnly={isViewMode}
                          setValue={(val) =>
                            handleFlightChange(index, "observer", val)
                          }
                          tableName="users"
                          itemData={{
                            filters: JSON.stringify({
                              is_active_eq: true,
                              "emp_category_id.category_name_eq": "Cockpit",
                            }),
                            display_options: `["emp_name", "emp_code"]`,
                            return_value: ["emp_name", "emp_code"],
                          }}
                        />
                      </Grid.Col>

                      <Grid.Col span={12}>
                        {!isViewMode && (
                          <Group position="right" mt="sm">
                            <Badge
                              color="red"
                              variant="outline"
                              style={{ cursor: "pointer" }}
                              onClick={() => handleRemoveFlight(index)}
                              leftSection={<IconTrash size={12} />}
                            >
                              Remove
                            </Badge>
                          </Group>
                        )}
                      </Grid.Col>
                    </Grid>
                  </Box>
                </Accordion.Panel>
              </Accordion.Item>
            );
          })}
        </Accordion>
      </Grid.Col>

      {/* Crew Details */}
      <Grid.Col span={12}>
        <h3>Crew Details</h3>
      </Grid.Col>

      <Grid.Col span={4}>
        <SelectWithCleared
          label="Name"
          placeholder="Select Crew"
          value={form.crew_name}
          readOnly={isViewMode}
          setValue={(selected) => {
            if (selected) {
              const [, empId] = selected.split(" - ");
              handleChange("crew_name", selected);
              handleChange("emp_id", empId);
            } else {
              handleChange("crew_name", "");
              handleChange("emp_id", "");
            }
          }}
          tableName="users"
          itemData={{
            filters: `{"is_active_eq": true, "emp_category_id.category_name_eq": "Cabin"}`,
            display_options: `["emp_name", "emp_code"]`,
            return_value: ["emp_name", "emp_code"],
          }}
        />
      </Grid.Col>

      <Grid.Col span={4}>
        <TextInput
          disabled
          label="Emp ID"
          placeholder="Enter Emp No"
          value={form.emp_id}
        />
      </Grid.Col>

      <Grid.Col span={4}>
        <Select
          label="Position"
          data={["SCC", "CC"]}
          placeholder="Enter Position"
          readOnly={isViewMode}
          value={form.position}
          onChange={(val) => handleChange("position", val)}
          clearable
          searchable
        />
      </Grid.Col>

      <Grid.Col span={4}>
        <TimeInputComp
          label="In Time"
          value={form.in_time}
          error={errors.in_time}
          readOnly={isViewMode}
          setValue={(e) => handleChange("in_time", e?.target?.value)}
        />
      </Grid.Col>

      <Grid.Col span={4}>
        <TimeInputComp
          label="Out Time"
          value={form.out_time}
          error={errors.out_time}
          readOnly={isViewMode}
          setValue={(e) => handleChange("out_time", e?.target?.value)}
        />
      </Grid.Col>

      <Grid.Col span={12}>
        <h3>Signature</h3>
        <Group>
          <Button
            variant="outline"
            readOnly={isViewMode}
            mt="xs"
            w={"40%"}
            color={
              isViewMode
                ? form.crew_signature
                  ? COLORS.primary
                  : "orange"
                : COLORS.primary
            }
            onClick={() => {
              if (isViewMode) {
                if (form.crew_signature) {
                  setViewCrewSignatureModal(true);
                }
              } else {
                setSigVisibleCrew(true);
              }
            }}
          >
            {isViewMode
              ? !form.crew_signature
                ? "No Signature"
                : "View Signature"
              : !form.crew_signature
                ? "Add Signature"
                : !recordId
                  ? "Signature Added"
                  : "Update Signature"}
          </Button>
        </Group>
      </Grid.Col>

      <Grid.Col span={12}>
        <Textarea
          label="Remark"
          placeholder="Enter Remark"
          readOnly={isViewMode}
          value={form.remark}
          onChange={(e) => handleChange("remark", e.target.value)}
        />
      </Grid.Col>

      <Grid.Col span={12}>
        <Group gap={3} align="center">
          <Checkbox
            checked={form.confirmation}
            disabled={isViewMode}
            onChange={(e) => handleChange("confirmation", e.target.checked)}
            label="I confirm and acknowledge the"
          />

          <Text
            span
            c={COLORS.primary}
            td="underline"
            fz="14px"
            style={{ cursor: "pointer" }}
            onClick={() => setPointersModalOpen(true)}
          >
            listed points
          </Text>
        </Group>
      </Grid.Col>

      {/* <Grid.Col span={12}>
        <h3>Signature of SCC</h3>
        <Button
          mt="xs"
          variant="outline"
          w={"100%"}
          color={
            isViewMode
              ? form.scc_signature
                ? COLORS.primary
                : "orange"
              : COLORS.primary
          }
          onClick={() => {
            if (isViewMode) {
              if (form.scc_signature) {
                setViewSccSignatureModal(true);
              }
            } else {
              setSigVisibleSCC(true);
            }
          }}
        >
          {isViewMode
            ? !form.scc_signature
              ? "No Signature"
              : "View Signature"
            : !form.scc_signature
              ? "Add Signature"
              : !recordId
                ? "Signature Added"
                : "Update Signature"}
        </Button>
      </Grid.Col> */}

      <Group position="right" mt="md">
        <Button variant="light" color="gray" onClick={onClose}>
          Cancel
        </Button>
        <Button
          color={COLORS.green}
          onClick={handleSubmit}
          disabled={
            (
              (role === "scc" || role === "cc") &&   // apply only for SCC/CC
              recordId &&
              originalHasAllValues &&
              !requiredFieldsChanged
            )
            ||
            !form.confirmation ||
            createMutation.isPending ||
            updateMutation.isPending
          }
          loading={createMutation.isPending || updateMutation.isPending}
        >
          {recordId ? "Update" : "Save"}
        </Button>
      </Group>

      <ModalComp
        opened={pointersModalOpen}
        onClose={() => setPointersModalOpen(false)}
        title="Acknowledgement Points"
        size="lg"
        centered
        padding={20}
      >
        <List
          spacing="sm"
          size="sm"
          center
          icon={<IconCircleFilled color={COLORS.primary} size={8} />}
        >
          {acknowledgmentPoints.map((point, idx) => (
            <List.Item key={idx}>{point}</List.Item>
          ))}
        </List>
      </ModalComp>

      <ModalComp
        opened={viewCrewSignatureModal}
        onClose={() => setViewCrewSignatureModal(false)}
        title="Crew Signature"
        size="md"
        centered
      >
        {form.crew_signature ? (
          <Box ta="center">
            <Image
              src={form.crew_signature}
              alt="Crew Signature"
              fit="contain"
              radius="md"
              height={300}
              withPlaceholder
            />
          </Box>
        ) : (
          <Text ta="center" c="dimmed">
            No signature available
          </Text>
        )}
      </ModalComp>

      <DigitalSignature
        sigCanvas={sigCanvasCrew}
        visible={sigVisibleCrew}
        onClose={() => setSigVisibleCrew(false)}
        onSave={(dataURL) => {
          setForm((prev) => ({ ...prev, crew_signature: dataURL }));
          setSigVisibleCrew(false);
        }}
      />
    </Grid>
  );
};

export default CrewMovementSheetDrawer;
